import React, { useEffect, useState } from "react";
import { Loader2, ShoppingCart, BookOpen, Eye } from "lucide-react";
import api, { API } from "../lib/api";

export const COVERS = {
  "10y": "https://i0.wp.com/incarcerated.com.au/wp-content/uploads/2026/09/my-book.png?fit=683%2C1024&ssl=1",
  lag: "/images/life-after-the-gates.jpg",
};

const BOOK_INFO = [
  {
    key: "ebook_10y_aud",
    book: "10y",
    title: "10 Years in 10 Prisons",
    blurb: "A decade behind bars across ten different prisons \u2014 the struggles, the people, the places and the lessons learnt along the way.",
  },
  {
    key: "ebook_lag_aud",
    book: "lag",
    title: "Life After the Gates",
    tag: "New Book",
    blurb: "Walking out was only the beginning. Coming home, rebuilding trust, finding work and facing the stigma \u2014 the world moved on, and I had to learn how to live in it again.",
  },
];

const money = (n) => (n == null ? "" : `A$${n.toFixed(2)}`);

const useCheckout = () => {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const buy = async (lookup_key, from_session_id) => {
    setBusy(lookup_key);
    setErr("");
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, from_session_id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(null);
    }
  };
  return { busy, err, buy };
};

export const UpsellCard = ({ upsell, sessionId }) => {
  const { busy, err, buy } = useCheckout();
  return (
    <div data-testid="upsell-card" className="mt-10 rounded-md border-2 border-[#e0a82e] bg-[#242424] p-6 text-left">
      <p className="font-display text-sm uppercase tracking-widest text-[#e0574c]">Complete the bundle</p>
      <div className="mt-3 flex items-center gap-4">
        <img src={COVERS[upsell.book]} alt={`${upsell.title} cover`} className="w-20 shrink-0 rounded border border-white/10 shadow-xl" />
        <div>
          <h3 className="font-display text-xl uppercase tracking-wide text-[#e0a82e]">Add {upsell.title}</h3>
          <p className="mt-1 text-sm text-[#c8c8c8]">
            Get the other e-book for just <strong className="text-[#e0a82e]">{money(upsell.price)}</strong>
            {upsell.bundle_price != null && <> &mdash; both books for the bundle price of {money(upsell.bundle_price)}.</>}
          </p>
        </div>
      </div>
      <button
        onClick={() => buy(upsell.lookup_key, sessionId)}
        disabled={!!busy}
        data-testid="upsell-buy"
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded bg-[#e0a82e] px-5 py-3 font-display text-sm font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors hover:bg-[#f2b93f] disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />} Add it for {money(upsell.price)}
      </button>
      {err && <p className="mt-2 text-sm text-[#f08a80]">{err}</p>}
    </div>
  );
};

const BuyButton = ({ product, busy, onBuy, label, testid }) => {
  const available = product?.available;
  return (
    <button
      onClick={() => available && onBuy(product.lookup_key)}
      disabled={!available || !!busy}
      data-testid={testid}
      className="inline-flex w-full items-center justify-center gap-2 rounded bg-[#e0a82e] px-5 py-3 font-display text-sm font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors hover:bg-[#f2b93f] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {busy === product?.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
      {!product ? "Loading..." : available ? `${label} \u2014 ${money(product.price)}` : "Coming Soon"}
    </button>
  );
};

const BookCard = ({ info, product, busy, onBuy }) => (
  <div data-testid={`book-card-${info.book}`} className="flex flex-col rounded-md border border-white/10 bg-[#242424] p-5 text-center">
    <img src={COVERS[info.book]} alt={`${info.title} book cover`} className="mx-auto w-44 rounded-md border border-white/10 shadow-2xl" loading="lazy" />
    {info.tag && <span className="mx-auto mt-4 rounded bg-[#e0574c] px-2 py-0.5 text-[11px] font-600 uppercase tracking-wide text-white">{info.tag}</span>}
    <h3 className="mt-3 font-display text-xl uppercase tracking-wide text-[#e0a82e]">{info.title}</h3>
    <p className="mt-2 flex-1 text-sm leading-relaxed text-[#c8c8c8]">{info.blurb}</p>
    <a
      href={`${API}/ebooks/preview/${info.book}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`preview-${info.book}`}
      className="mx-auto mt-4 inline-flex items-center gap-1.5 font-display text-sm uppercase tracking-wide text-[#e0a82e] transition-colors hover:text-[#f2b93f]"
    >
      <Eye className="h-4 w-4" /> Read a free preview
    </a>
    <div className="mt-5"><BuyButton product={product} busy={busy} onBuy={onBuy} label="Buy now" testid={`buy-${info.book}`} /></div>
  </div>
);

const BundleCard = ({ product, singles, busy, onBuy }) => {
  const full = singles.reduce((s, p) => s + (p?.price || 0), 0);
  const save = product?.price != null && full > product.price ? full - product.price : 0;
  return (
    <div data-testid="bundle-card" className="relative mt-6 flex flex-col items-center gap-6 rounded-md border-2 border-[#e0a82e] bg-[#242424] p-6 md:flex-row">
      <div className="flex shrink-0 gap-2">
        {Object.values(COVERS).map((c) => <img key={c} src={c} alt="" className="w-20 rounded border border-white/10 shadow-xl md:w-24" loading="lazy" />)}
      </div>
      <div className="flex-1 text-center md:text-left">
        <p className="font-display text-sm uppercase tracking-widest text-[#e0574c]">Book Bundle Deal</p>
        <h3 className="mt-1 whitespace-nowrap font-display text-xl uppercase tracking-wide text-[#e0a82e] md:text-2xl">Get both e-books</h3>
        <p className="mt-2 text-sm text-[#c8c8c8]">10 Years in 10 Prisons + Life After the Gates together.</p>
        {save > 0 && <p data-testid="bundle-saving" className="mt-1 text-sm font-600 text-[#e0a82e]">Save {money(save)} <span className="font-400 text-[#8f8f8f] line-through">{money(full)}</span></p>}
      </div>
      <div className="w-full md:w-60"><BuyButton product={product} busy={busy} onBuy={onBuy} label="Buy bundle" testid="buy-bundle" /></div>
    </div>
  );
};

const BookShop = () => {
  const [products, setProducts] = useState({});
  const { busy, err, buy } = useCheckout();

  useEffect(() => {
    api.get("/ebooks").then(({ data }) => setProducts(Object.fromEntries(data.map((p) => [p.lookup_key, p])))).catch(() => {});
  }, []);

  return (
    <div data-testid="book-shop">
      <div className="grid gap-6 sm:grid-cols-2">
        {BOOK_INFO.map((info) => <BookCard key={info.key} info={info} product={products[info.key]} busy={busy} onBuy={buy} />)}
      </div>
      <BundleCard product={products.ebook_bundle_aud} singles={BOOK_INFO.map((i) => products[i.key])} busy={busy} onBuy={buy} />
      {err && <p className="mt-3 text-sm text-[#f08a80]" data-testid="book-shop-error">{err}</p>}
      <p className="mt-4 flex items-center gap-2 text-sm italic text-[#a0a0a0]">
        <BookOpen className="h-4 w-4 shrink-0" /> Secure checkout by Stripe. Prices include GST. After payment your download unlocks instantly on this site and a copy of the link is emailed to you.
      </p>
    </div>
  );
};

export default BookShop;
